import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import "./SankeyChart.scss";

const W = 1000;
const NODE_W = 12;
const PAD = 8;
const TOP = 22;
const LABEL_H = 17; // a node's slot is never shorter than its label, so labels never overprint
const LABEL_SPACE = 200;

/**
 * The one scale (px per unit) every column shares — bands must be the same thickness at
 * both ends — chosen as the largest at which each column fits. Nodes too small to hold a
 * label are given a label-high slot and the rest share what is left.
 */
function fitScale(cols, usable) {
  return Math.min(...cols.map((ns) => {
    if (!ns.length) return Infinity;
    const room = usable - PAD * (ns.length - 1);
    const total = ns.reduce((t, n) => t + n.value, 0);
    let scale = room / total;
    let small = 0;
    const tooSmall = (k) => ns.filter((n) => n.value * k < LABEL_H);
    for (let i = 0; i < ns.length; i += 1) {
      const next = tooSmall(scale);
      if (next.length === small) break;
      small = next.length;
      const rest = total - next.reduce((t, n) => t + n.value, 0);
      scale = rest ? Math.max((room - LABEL_H * small) / rest, 0.0001) : Infinity;
    }
    return scale;
  }));
}

/**
 * SankeyChart — flows between columns of nodes (e.g. channel → landing page → outcome),
 * each band as thick as the number it carries. Dependency-free SVG, drawn in a fixed
 * 1000-wide coordinate space and scaled to its container.
 *
 * Hovering a node isolates everything that passes through it. Small nodes keep a
 * label-high slot, so a long tail of 1% sources stays legible; the drawing grows taller
 * rather than letting labels collide.
 *
 * @param columns  [{ title, nodes: [{ id, label, color }] }] — node ids unique across columns;
 *                 order within a column is the caller's (sort it by size)
 * @param links    [{ source, target, value, color? }] between adjacent columns. A band takes
 *                 its own `color`, else its source node's.
 */
const SankeyChart = ({ columns, links, height = 380, formatValue, unit }) => {
  const [hover, setHover] = useState(null);
  const fv = formatValue || ((n) => Number(n).toLocaleString("en-GB"));

  const layout = useMemo(() => {
    const valueIn = {};
    const valueOut = {};
    links.forEach((l) => {
      valueOut[l.source] = (valueOut[l.source] || 0) + l.value;
      valueIn[l.target] = (valueIn[l.target] || 0) + l.value;
    });
    const cols = columns.map((c) => c.nodes
      .map((n) => ({ ...n, value: Math.max(valueIn[n.id] || 0, valueOut[n.id] || 0) }))
      .filter((n) => n.value > 0));
    const usable = height - TOP - 6;
    const scale = fitScale(cols, usable);
    const slotOf = (n) => Math.max(n.value * scale, LABEL_H);
    const colHeight = (ns) => ns.reduce((t, n) => t + slotOf(n), 0) + PAD * Math.max(ns.length - 1, 0);
    const drawn = Math.max(usable, ...cols.map(colHeight));
    const step = cols.length > 1 ? (W - LABEL_SPACE - NODE_W) / (cols.length - 1) : 0;
    const byId = {};
    cols.forEach((ns, ci) => {
      let y = TOP + (drawn - colHeight(ns)) / 2;
      ns.forEach((n) => {
        const h = Math.max(n.value * scale, 2);
        const slot = slotOf(n);
        byId[n.id] = { ...n, col: ci, x: ci * step, y: y + (slot - h) / 2, h };
        y += slot + PAD;
      });
    });
    const live = links.filter((l) => byId[l.source] && byId[l.target] && l.value > 0);
    // Each node's outgoing bands stack in the order of where they go, its incoming bands
    // in the order of where they came from — the arrangement that crosses least.
    const outOffset = {};
    const inOffset = {};
    const offsets = new Map();
    [...live].sort((a, b) => byId[a.target].y - byId[b.target].y).forEach((l) => {
      const th = Math.max(l.value * scale, 1);
      offsets.set(l, { out: outOffset[l.source] || 0 });
      outOffset[l.source] = (outOffset[l.source] || 0) + th;
    });
    [...live].sort((a, b) => byId[a.source].y - byId[b.source].y).forEach((l) => {
      const th = Math.max(l.value * scale, 1);
      offsets.get(l).in = inOffset[l.target] || 0;
      inOffset[l.target] = (inOffset[l.target] || 0) + th;
    });
    const bands = live
      .map((l) => {
        const s = byId[l.source];
        const t = byId[l.target];
        const th = Math.max(l.value * scale, 1);
        const o = offsets.get(l);
        const y0 = s.y + o.out + th / 2;
        const y1 = t.y + o.in + th / 2;
        const x0 = s.x + NODE_W;
        const x1 = t.x;
        const xm = (x0 + x1) / 2;
        return {
          ...l, th, sourceLabel: s.label, targetLabel: t.label,
          d: `M${x0},${y0} C${xm},${y0} ${xm},${y1} ${x1},${y1}`,
          color: l.color || s.color || "#94a3b8",
        };
      })
      .sort((a, b) => b.th - a.th); // thin bands drawn last, on top, so they stay visible
    const grand = cols[0] ? cols[0].reduce((t, n) => t + n.value, 0) : 0;
    return { nodes: Object.values(byId), bands, step, grand, svgHeight: TOP + drawn + 6 };
  }, [columns, links, height]);

  const touches = (b) => hover && (b.source === hover || b.target === hover);
  return (
    <div className="sankey">
      <svg viewBox={`0 0 ${W} ${layout.svgHeight}`} role="img" aria-label="Flow diagram">
        {columns.map((c, ci) => (
          <text key={c.title} className="sankey__col-title" x={ci * layout.step} y={12}>{c.title}</text>
        ))}
        <g>
          {layout.bands.map((b) => (
            <path
              key={`${b.source}>${b.target}`}
              d={b.d}
              className="sankey__band"
              stroke={b.color}
              strokeWidth={b.th}
              style={{ opacity: hover ? (touches(b) ? 0.65 : 0.05) : 0.34 }}
            >
              <title>{`${b.sourceLabel || b.source} → ${b.targetLabel || b.target}: ${fv(b.value)}${unit ? ` ${unit}` : ""}`}</title>
            </path>
          ))}
        </g>
        <g>
          {layout.nodes.map((n) => {
            const share = layout.grand ? Math.round((n.value / layout.grand) * 100) : 0;
            const dim = hover && hover !== n.id && !layout.bands.some((b) => touches(b) && (b.source === n.id || b.target === n.id));
            return (
              <g
                key={n.id}
                className="sankey__node"
                style={{ opacity: dim ? 0.35 : 1 }}
                onMouseEnter={() => setHover(n.id)}
                onMouseLeave={() => setHover(null)}
              >
                <rect x={n.x} y={n.y} width={NODE_W} height={n.h} rx={2} fill={n.color || "#64748b"} />
                <text x={n.x + NODE_W + 6} y={n.y + n.h / 2} dominantBaseline="middle" className="sankey__label">
                  <tspan className="sankey__label-name">{n.label}</tspan>
                  <tspan className="sankey__label-value" dx={6}>{fv(n.value)}{share ? ` · ${share}%` : ""}</tspan>
                </text>
                <title>{`${n.label}: ${fv(n.value)}${unit ? ` ${unit}` : ""}`}</title>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
};

SankeyChart.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    nodes: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired, label: PropTypes.string.isRequired, color: PropTypes.string,
    })).isRequired,
  })).isRequired,
  links: PropTypes.arrayOf(PropTypes.shape({
    source: PropTypes.string.isRequired, target: PropTypes.string.isRequired, value: PropTypes.number.isRequired,
    color: PropTypes.string,
  })).isRequired,
  height: PropTypes.number,
  formatValue: PropTypes.func,
  unit: PropTypes.string,
};

export default SankeyChart;
